import React, {useContext, useMemo} from 'react'
import {UserRole} from '@common/models'
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react'
import {useTranslation} from 'react-i18next'
import {Outlet} from 'react-router-dom'
import styled from 'styled-components'

import {AuthLayout} from '~/components/layout/authLayout/AuthLayout'
import {FeatureFlagProvider} from '~/components/general/FeatureFlagContext'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {AuthContext} from '~/auth/AuthContext'
import {MaterialsContextProvider} from '~/components/educationalMaterials/MaterialsContext'
import {PatientsContextProvider} from '~/components/patients/PatientsContext'
import {PractitionerContextProvider} from '~/components/profile/PractitionerContext'
import {PartnerAdministratorContextProvider} from '~/components/profile/PartnerAdministratorContext'
import {QuestionnairesContextProvider} from '~/components/questionnaires/QuestionnairesContext'
import {getUserRoles} from '~/utils/simulateJWT'

const StyledButton = styled(Button)`
  margin-top: 1rem;
`

type AuthenticatedContentProps = {
  hasValidRole: boolean
  hasBothRoles: boolean
  shouldRenderPractitioner: boolean
  shouldRenderPartnerAdmin: boolean
}

const AuthenticatedContent: React.FC<AuthenticatedContentProps> = ({
  hasValidRole,
  hasBothRoles,
  shouldRenderPractitioner,
  shouldRenderPartnerAdmin,
}) => {
  const authContext = useContext(AuthContext)
  const {t} = useTranslation<string>()

  if (!hasValidRole) {
    return (
      <AuthLayout>
        <Typography>
          {t(
            'If you are a patient who would like to see their health data, please download our mobile application and log in to it.',
          )}
        </Typography>
        <StyledButton onClick={authContext.signOut}>
          {t('Sign out')}
        </StyledButton>
      </AuthLayout>
    )
  }

  if (hasBothRoles) {
    return (
      <PractitionerContextProvider>
        <PartnerAdministratorContextProvider>
          <FeatureFlagProvider>
            <MaterialsContextProvider>
              <QuestionnairesContextProvider>
                <PatientsContextProvider>
                  <Outlet />
                </PatientsContextProvider>
              </QuestionnairesContextProvider>
            </MaterialsContextProvider>
          </FeatureFlagProvider>
        </PartnerAdministratorContextProvider>
      </PractitionerContextProvider>
    )
  }

  if (shouldRenderPractitioner) {
    return (
      <PractitionerContextProvider>
        <FeatureFlagProvider>
          <MaterialsContextProvider>
            <QuestionnairesContextProvider>
              <PatientsContextProvider>
                <Outlet />
              </PatientsContextProvider>
            </QuestionnairesContextProvider>
          </MaterialsContextProvider>
        </FeatureFlagProvider>
      </PractitionerContextProvider>
    )
  }

  if (shouldRenderPartnerAdmin) {
    return (
      <PartnerAdministratorContextProvider>
        <FeatureFlagProvider>
          <Outlet />
        </FeatureFlagProvider>
      </PartnerAdministratorContextProvider>
    )
  }

  return null
}

export const ProtectedPageTemplate: React.FC = () => {
  const {accounts} = useMsal()
  const {t} = useTranslation<string>()

  // Get roles from either real JWT claims or simulated local roles
  const jwtClaims = accounts[0]?.idTokenClaims as
    | Record<string, string>
    | undefined
  const roles = getUserRoles(jwtClaims)

  const isPractitioner = useMemo(
    () => roles.split(',').includes(UserRole.PRACTITIONER),
    [roles],
  )

  const isPartnerAdministrator = useMemo(
    () => roles.split(',').includes(UserRole.PARTNER_ADMINISTRATOR),
    [roles],
  )

  const hasValidRole = isPractitioner || isPartnerAdministrator

  // Determine which context to render based on role combinations
  const shouldRenderPartnerAdmin = isPartnerAdministrator
  const shouldRenderPractitioner = isPractitioner
  const hasBothRoles = isPractitioner && isPartnerAdministrator

  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedContent
          hasValidRole={hasValidRole}
          hasBothRoles={hasBothRoles}
          shouldRenderPractitioner={shouldRenderPractitioner}
          shouldRenderPartnerAdmin={shouldRenderPartnerAdmin}
        />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <AuthLayout>
          <Typography>{t('You need to login to access this page.')}</Typography>
          <StyledButton onClick={useContext(AuthContext).signIn}>
            {t('Login')}
          </StyledButton>
        </AuthLayout>
      </UnauthenticatedTemplate>
    </>
  )
}
